// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type ReactNode, useMemo, useState } from 'react';

import { useGeneralContext } from 'helpers/useGeneralContext';
import type { HighlightableObjectName } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';

export type BendPreviewContextType = {
  highlightedObject: HighlightableObjectName | null;
  setHighlightedObject: (object: HighlightableObjectName | null) => void;
};

const BendPreviewContext = createContext<BendPreviewContextType | undefined>(undefined);

/**
 * Context that acts as an interface between React and the BendPreviewRenderer.
 * It provides the currently highlighted object and a function to update it.
 */
export const BendPreviewContextProvider = ({ children }: { children: ReactNode }) => {
  const [highlightedObject, setHighlightedObject] = useState<HighlightableObjectName | null>(null);
  const value = useMemo(() => ({ highlightedObject, setHighlightedObject }), [highlightedObject]);
  return <BendPreviewContext.Provider value={value}>{children}</BendPreviewContext.Provider>;
};

export const useBendPreviewContext = () => useGeneralContext(BendPreviewContext, 'BendPreviewContext');
